/**
 * Main Application Layout
 * Apple-inspired sidebar + top header
 */

import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  Anchor,
  Home,
  PlusCircle,
  List,
  Bell,
  User,
  Menu,
  X,
  LogOut,
  ChevronDown,
  MapPin,
  Briefcase,
  BarChart3,
  Users,
  ShieldCheck,
  Mail,
  Ship,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function MainLayout() {
  const { user, logout, isYachtOwner, isAdmin } = useAuth();
  const { unreadCount } = useNotifications();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = isAdmin
    ? [
        { path: '/admin', label: 'Dashboard', icon: Home },
        { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/admin/users', label: 'Users', icon: Users },
        { path: '/admin/requests', label: 'Requests', icon: List },
        { path: '/admin/helpers', label: 'Verifications', icon: ShieldCheck },
        { path: '/admin/messaging', label: 'Messaging', icon: Mail },
      ]
    : isYachtOwner
    ? [
        { path: '/owner', label: 'Dashboard', icon: Home },
        { path: '/owner/yachts', label: 'My Yacht', icon: Ship },
        { path: '/owner/request/new', label: 'New Request', icon: PlusCircle },
        { path: '/owner/requests', label: 'My Requests', icon: List },
      ]
    : [
        { path: '/helper', label: 'Dashboard', icon: Home },
        { path: '/helper/requests', label: 'Available Jobs', icon: MapPin },
        { path: '/helper/services', label: 'My Services', icon: Briefcase },
      ];

  const isActive = (path) => location.pathname === path;
  const dashboardPath = isAdmin ? '/admin' : isYachtOwner ? '/owner' : '/helper';

  return (
    <div className="min-h-screen bg-navy-100">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 md:w-56 bg-navy-50 z-30"
        style={{ borderRight: '1px solid rgba(0,0,0,0.06)' }}
      >
        <div className="h-16 flex items-center px-5">
          <Link to={dashboardPath} className="flex items-center">
            <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-8 w-8 rounded" />
            <span className="ml-2 text-lg font-semibold text-navy-900 tracking-tight">
              AnchorPals
            </span>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-3 py-2.5 text-sm font-normal rounded-apple transition-all duration-300 ease-apple ${
                isActive(item.path)
                  ? 'bg-white text-primary-600 shadow-apple-sm'
                  : 'text-navy-600 hover:bg-white/60 hover:text-navy-900'
              }`}
            >
              <item.icon className={`h-[18px] w-[18px] mr-3 ${isActive(item.path) ? 'text-primary-600' : 'text-navy-500'}`} />
              {item.label}
            </Link>
          ))}
        </nav>
      </aside>

      {/* Right side: header + content */}
      <div className="md:ml-56">
        {/* Top Header */}
        <nav className="glass sticky top-0 z-40"
          style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
        >
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-14">
              {/* Mobile: Hamburger + Logo */}
              <div className="flex items-center md:hidden">
                <button
                  onClick={() => setMobileMenuOpen(true)}
                  className="p-2 text-navy-500 hover:text-navy-900 rounded-apple transition-colors duration-300"
                >
                  <Menu className="h-5 w-5" />
                </button>
                <Link to={dashboardPath} className="flex items-center ml-2">
                  <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-8 w-8 rounded" />
                  <span className="ml-2 text-lg font-semibold text-navy-900 tracking-tight">
                    AnchorPals
                  </span>
                </Link>
              </div>

              {/* Desktop spacer */}
              <div className="hidden md:block" />

              {/* Right: Bell + Profile */}
              <div className="flex items-center space-x-3">
                <Link
                  to="/notifications"
                  className="relative p-2 text-navy-500 hover:text-navy-900 rounded-full transition-colors duration-300"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 h-4 w-4 bg-red-500 text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>

                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center space-x-2 p-1.5 text-navy-700 hover:bg-navy-100 rounded-full transition-colors duration-300"
                  >
                    <div className="h-8 w-8 bg-navy-200 rounded-full flex items-center justify-center overflow-hidden">
                      {user?.profile_photo_url ? (
                        <img
                          src={user.profile_photo_url.startsWith('http') ? user.profile_photo_url : `${UPLOADS_BASE_URL}${user.profile_photo_url}`}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-navy-600 font-medium text-sm">
                          {user?.first_name?.[0]}{user?.last_name?.[0]}
                        </span>
                      )}
                    </div>
                    <span className="hidden sm:block text-sm font-normal text-navy-700">
                      {user?.first_name}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-navy-400" />
                  </button>

                  {userMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-52 bg-white rounded-apple-lg py-1 z-20"
                        style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)' }}
                      >
                        <div className="px-4 py-3 border-b border-navy-200">
                          <p className="text-sm font-medium text-navy-900">
                            {user?.first_name} {user?.last_name}
                          </p>
                          <p className="text-xs text-navy-500 capitalize mt-0.5">
                            {user?.user_type?.replace('_', ' ')}
                          </p>
                        </div>
                        {isAdmin && (
                          <Link
                            to="/admin"
                            className="flex items-center px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 transition-colors"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <BarChart3 className="h-4 w-4 mr-3 text-navy-400" />
                            Admin Panel
                          </Link>
                        )}
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-2.5 text-sm text-navy-700 hover:bg-navy-50 transition-colors"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          <User className="h-4 w-4 mr-3 text-navy-400" />
                          Edit Profile
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Sign Out
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="px-4 sm:px-6 lg:px-10 py-8 max-w-6xl">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 w-72 bg-navy-50 shadow-apple-lg flex flex-col">
            <div className="h-16 flex items-center justify-between px-5">
              <Link
                to={dashboardPath}
                className="flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                <img src="/anchorpals-logo.jpg" alt="AnchorPals" className="h-8 w-8 rounded" />
                <span className="ml-2 text-lg font-semibold text-navy-900 tracking-tight">
                  AnchorPals
                </span>
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-navy-400 hover:text-navy-900 rounded-apple transition-colors duration-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-0.5">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center px-3 py-3 text-base font-normal rounded-apple transition-all duration-300 ease-apple ${
                    isActive(item.path)
                      ? 'bg-white text-primary-600 shadow-apple-sm'
                      : 'text-navy-600 hover:bg-white/60 hover:text-navy-900'
                  }`}
                >
                  <item.icon className={`h-5 w-5 mr-3 ${isActive(item.path) ? 'text-primary-600' : 'text-navy-500'}`} />
                  {item.label}
                </Link>
              ))}
            </nav>
          </aside>
        </div>
      )}
    </div>
  );
}

export default MainLayout;
